import type { AggregateBucket, DashboardWidget } from "@gjwtrack/domain";
import { ExternalLink, RefreshCw, Settings2, X } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { client, useClient } from "../../lib/client.ts";
import { IssueRow } from "../IssueRow.tsx";
import { MarkdownView } from "../MarkdownView.tsx";

const ROW = 36;
const issuesHref = (q: string): string => (q ? `/issues?q=${encodeURIComponent(q)}` : "/issues");

/** Chrome shared by every widget: title bar (the drag handle while editing), actions, scrolling body. */
export function WidgetFrame({ title, editing, onSettings, onRemove, actions, children }: { title: string; editing: boolean; onSettings: () => void; onRemove: () => void; actions?: ReactNode; children: ReactNode }) {
  return (
    <div className="widget">
      <div className={`widget-head ${editing ? "widget-drag" : ""}`}>
        <span className="min-w-0 flex-1 truncate font-semibold">{title}</span>
        <span className="flex shrink-0 items-center gap-0.5">
          {actions}
          {editing && (
            <>
              <button className="icon-btn" title="Widget settings" onClick={onSettings} onMouseDown={(e) => e.stopPropagation()}>
                <Settings2 size={14} />
              </button>
              <button className="icon-btn" title="Remove widget" onClick={onRemove} onMouseDown={(e) => e.stopPropagation()}>
                <X size={14} />
              </button>
            </>
          )}
        </span>
      </div>
      <div className="widget-body">{children}</div>
    </div>
  );
}

export function defaultTitle(w: DashboardWidget): string {
  if (w.config.title) return w.config.title;
  switch (w.type) {
    case "issues":
      return w.config.query || "All issues";
    case "notes":
      return "Notes";
    case "counters":
      return "Issue counts";
    case "distribution":
      return `Issues per ${w.config.by}`;
  }
}

/** Renders one widget's content. `sub` is the live-query id prefix reserved for this widget on this page. */
export function WidgetBody({ widget, sub }: { widget: DashboardWidget; sub: string }) {
  switch (widget.type) {
    case "issues":
      return <IssuesWidget sub={sub} query={widget.config.query} limit={widget.config.limit} />;
    case "notes":
      return <MarkdownView text={widget.config.markdown} className="px-3 py-2" />;
    case "counters":
      return <CountersWidget sub={sub} items={widget.config.items} />;
    case "distribution":
      return <DistributionWidget query={widget.config.query} by={widget.config.by} limit={widget.config.limit} refreshKey={sub} />;
  }
}

/** Extra title-bar actions per widget type (the Issues page link, refresh). */
export function WidgetActions({ widget, sub }: { widget: DashboardWidget; sub: string }) {
  if (widget.type === "issues") {
    return (
      <Link to={issuesHref(widget.config.query)} className="icon-btn" title="Open in Issues" onMouseDown={(e) => e.stopPropagation()}>
        <ExternalLink size={14} />
      </Link>
    );
  }
  if (widget.type === "distribution") {
    return (
      <button className="icon-btn" title="Refresh" onClick={() => refreshDistribution(sub)} onMouseDown={(e) => e.stopPropagation()}>
        <RefreshCw size={14} />
      </button>
    );
  }
  return null;
}

// ----------------------------------------------------------------- issues

function IssuesWidget({ sub, query, limit }: { sub: string; query: string; limit: number }) {
  useEffect(() => {
    client.openView(sub, query, limit);
    return () => client.closeView(sub);
  }, [sub, query, limit]);
  const view = useClient((s) => s.views[sub]);
  const schema = useClient((s) => s.schema);
  if (!view || !schema) return null;
  if (view.status === "error") return <div className="px-3 py-2 text-danger">{view.error}</div>;
  if (view.status === "loading" && view.ids.length === 0) return <div className="px-3 py-2 text-muted">loading…</div>;
  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-auto">
        {view.ids.length === 0 && <div className="px-3 py-3 text-center text-muted">No issues match.</div>}
        {view.ids.map((id) => (
          <div key={id} style={{ height: ROW }} className="widget-row">
            <IssueRow id={id} />
          </div>
        ))}
      </div>
      <div className="flex shrink-0 items-center gap-2 border-t border-line px-3 py-1 text-xs text-muted">
        <b className="text-ink tabular-nums">{view.total}</b> {view.total === 1 ? "issue" : "issues"}
        {view.total > view.ids.length && (
          <Link to={issuesHref(query)} className="text-link hover:underline">
            show all
          </Link>
        )}
        {view.badge > 0 && (
          <button className="ml-auto rounded bg-warn-soft px-1.5 text-warn" onClick={() => client.openView(sub, query, limit)} title="Changes happened outside the visible window">
            refresh
          </button>
        )}
        {view.status === "stale" && <span className="text-warn">offline</span>}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------- counters

function CountersWidget({ sub, items }: { sub: string; items: { label: string; query: string }[] }) {
  return (
    <div className="grid h-full auto-rows-fr gap-2 p-2" style={{ gridTemplateColumns: `repeat(${Math.min(items.length, 4)}, minmax(0, 1fr))` }}>
      {items.map((it, i) => (
        <Counter key={`${i}:${it.query}`} sub={`${sub}:${i}`} label={it.label} query={it.query} />
      ))}
    </div>
  );
}

function Counter({ sub, label, query }: { sub: string; label: string; query: string }) {
  useEffect(() => {
    client.openView(sub, query, 0);
    return () => client.closeView(sub);
  }, [sub, query]);
  const view = useClient((s) => s.views[sub]);
  return (
    <Link to={issuesHref(query)} className="counter" title={query || "all issues"}>
      <span className="text-3xl font-semibold tabular-nums leading-none">{view?.status === "error" ? "!" : view && (view.status !== "loading" || view.version > 0) ? view.total : "…"}</span>
      <span className="mt-1 truncate text-xs text-muted">{view?.status === "error" ? view.error : label}</span>
    </Link>
  );
}

// ----------------------------------------------------------------- distribution

const POLL_MS = 60_000;
const refreshers = new Map<string, () => void>();
const refreshDistribution = (key: string): void => refreshers.get(key)?.();

/** Bars per project / field value. Not live: polled every minute, on tab focus and on demand (see M12-PLAN). */
function DistributionWidget({ query, by, limit, refreshKey }: { query: string; by: string; limit: number; refreshKey: string }) {
  const [data, setData] = useState<{ total: number; buckets: AggregateBucket[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const bump = (): void => setTick((n) => n + 1);
    refreshers.set(refreshKey, bump);
    const timer = setInterval(bump, POLL_MS);
    const onVisible = (): void => {
      if (document.visibilityState === "visible") bump();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      refreshers.delete(refreshKey);
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refreshKey]);
  useEffect(() => {
    let cancelled = false;
    client.http
      .aggregate(query, by, limit)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setError(null);
      })
      .catch((e) => !cancelled && setError((e as Error).message));
    return () => {
      cancelled = true;
    };
  }, [query, by, limit, tick]);
  if (error) return <div className="px-3 py-2 text-danger">{error}</div>;
  if (!data) return <div className="px-3 py-2 text-muted">loading…</div>;
  if (data.buckets.length === 0) return <div className="px-3 py-3 text-center text-muted">No issues match.</div>;
  const max = Math.max(...data.buckets.map((b) => b.count));
  const valueQuery = (b: AggregateBucket): string | null => {
    if (b.key === "other") return null;
    const name = b.key === "" ? "{No " + by.toLowerCase() + "}" : /[\s:,]/.test(b.label) ? `{${b.label}}` : b.label;
    return `${query ? `${query} ` : ""}${by}: ${name}`.trim();
  };
  return (
    <div className="px-3 py-2">
      {data.buckets.map((b) => {
        const q = valueQuery(b);
        const bar = (
          <>
            <span className="w-32 shrink-0 truncate text-right" title={b.label}>
              {b.label}
            </span>
            <span className="relative h-4 min-w-0 flex-1 overflow-hidden rounded-sm bg-sunken">
              <span className={`absolute inset-y-0 left-0 rounded-sm ${b.colorId ? `chip-${((b.colorId - 1) % 8) + 1}` : ""}`} style={{ width: `${Math.max(2, (b.count / max) * 100)}%`, background: b.colorId ? "var(--chip-fg)" : "var(--accent)" }} />
            </span>
            <span className="w-10 shrink-0 text-right text-xs text-muted tabular-nums">{b.count}</span>
          </>
        );
        return q && b.key !== "" ? (
          <Link key={b.key} to={issuesHref(q)} className="flex items-center gap-2 py-0.5 hover:bg-surface">
            {bar}
          </Link>
        ) : (
          <div key={b.key} className="flex items-center gap-2 py-0.5">
            {bar}
          </div>
        );
      })}
      <div className="mt-1 text-right text-xs text-muted">
        <b className="text-ink tabular-nums">{data.total}</b> issues
      </div>
    </div>
  );
}
